// @ts-check
import {
  DEFAULT_GRID_PRESET,
  DEFAULT_GRID_PRESET_KEY,
  SMALLEST_GRID_PRESET,
  SMALLEST_GRID_PRESET_KEY,
  gridPresetSelectOptions,
  isKnownGridPresetKey,
  resolveGridPresetKey
} from '../../shared/grid-presets.js';

/**
 * Admin selects whose options come from {@link gridPresetSelectOptions}.
 * Stored keys that no longer name a preset fall back here instead of showing a blank row.
 *
 * @type {Array<{ id: string, hintId: string, includeAuto: boolean, fallbackKey: string, hint: string }>}
 */
export const GRID_PRESET_SELECTS = [
  {
    id: 'setting-default-grid-preset',
    hintId: 'setting-default-grid-preset-hint',
    includeAuto: false,
    fallbackKey: DEFAULT_GRID_PRESET_KEY,
    hint: `Initial piece count pre-selected in Create Room. Default ${DEFAULT_GRID_PRESET.label}.`
  },
  {
    id: 'setting-house-room-preset',
    hintId: 'setting-house-room-preset-hint',
    includeAuto: false,
    fallbackKey: SMALLEST_GRID_PRESET_KEY,
    hint: `Grid preset for the maintained house room. Default ${SMALLEST_GRID_PRESET.label}.`
  },
  {
    id: 'setting-bot-default-grid-preset',
    hintId: 'setting-bot-default-grid-preset-hint',
    includeAuto: true,
    fallbackKey: 'auto',
    hint: 'Grid preset for puzzles started by PuzzleBot. Default Auto.'
  }
];

/**
 * @param {string} selectId
 * @param {unknown} value
 * @returns {string}
 */
export function resolveStoredGridPreset(selectId, value) {
  const spec = GRID_PRESET_SELECTS.find((item) => item.id === selectId);
  if (!spec) return String(value ?? '');
  if (spec.includeAuto) {
    const key = String(value ?? '').trim();
    if (!key || key === 'auto' || isKnownGridPresetKey(key)) return key || 'auto';
    return 'auto';
  }
  return resolveGridPresetKey(value, spec.fallbackKey);
}

/**
 * Fills the three Settings preset selects from the shared preset list.
 *
 * @param {ParentNode & { getElementById: (id: string) => Element | null }} [root]
 * @returns {void}
 */
export function fillGridPresetSelects(root = document) {
  for (const spec of GRID_PRESET_SELECTS) {
    const select = root.getElementById(spec.id);
    if (select instanceof HTMLSelectElement) {
      const previous = select.value;
      while (select.options.length > 0) select.remove(0);
      for (const row of gridPresetSelectOptions({ includeAuto: spec.includeAuto })) {
        const option = document.createElement('option');
        option.value = row.value;
        option.textContent = row.label;
        select.append(option);
      }
      select.value = resolveStoredGridPreset(spec.id, previous || spec.fallbackKey);
    }
    const hint = root.getElementById(spec.hintId);
    if (hint) hint.textContent = spec.hint;
  }
}
